import { Navigate } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Wraps a page so it only renders when the admin's role allows it.
 *   <PermissionRoute resource="orders"><Orders /></PermissionRoute>
 * Without access the user sees an "Access denied" screen (never a blank page
 * or a stream of 403 toasts).
 */
export const PermissionRoute = ({ resource, action = 'view', children }) => {
  const { can } = useAuth();
  if (can(resource, action)) return children;

  return (
    <div className="flex items-center justify-center h-full p-8" data-testid="access-denied">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
          <ShieldOff size={26} className="text-orange-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Access denied
        </h2>
        <p className="text-gray-600 text-sm">
          Your role doesn&apos;t include access to this page. Ask a Super Admin to update your role if you need it.
        </p>
      </div>
    </div>
  );
};

/** Sends the user to the first page their role can open (default landing). */
export const HomeRedirect = () => {
  const { homePath } = useAuth();
  if (!homePath) {
    return (
      <div className="flex items-center justify-center h-full p-8" data-testid="no-access-at-all">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
            <ShieldOff size={26} className="text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            No access assigned
          </h2>
          <p className="text-gray-600 text-sm">
            Your account has no permissions yet (or your role is disabled). Contact a Super Admin.
          </p>
        </div>
      </div>
    );
  }
  return <Navigate to={homePath} replace />;
};
